var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ArrowUpward } from '@mui/icons-material';
import { Box, Checkbox, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { addCheckedItem, deleteCheckedItem, setSelectedItem, setSortSelectedItem } from 'app/actions/transport';
import { Autosizer, Protect, Scrollbar } from 'app/components';
import { TransportCircleIcon, TransportSquareIcon, TransportTriangleIcon } from 'app/icons';
import { checkedItemsSelector, selectedItemSelector, sortItemsSelector, sortSelectedItemSelector, visibleTransportColumnsSelector, } from 'app/selectors/transport';
import React, { createContext, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const TransportListContext = createContext(null);
const TransportList = () => {
    const dispatch = useDispatch();
    const selectedItem = useSelector(selectedItemSelector);
    const sortSelectedItem = useSelector(sortSelectedItemSelector);
    const checkedItems = useSelector(checkedItemsSelector);
    const sortItems = useSelector(sortItemsSelector);
    const columns = useSelector(visibleTransportColumnsSelector);
    const sortItemsIds = sortItems.map((item) => item.id).filter((id) => id != null);
    const isCheckedAll = sortItemsIds.length > 0 && sortItemsIds.every((id) => checkedItems.includes(id));
    const isIndeterminate = sortItemsIds.some((id) => checkedItems.includes(id)) && !isCheckedAll;
    const handleItemClick = (value) => () => {
        dispatch(setSelectedItem(value));
    };
    const handleSortClick = (value) => () => {
        dispatch(setSortSelectedItem(value));
    };
    const handleCheckboxClick = (id) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        dispatch(checkedItems.indexOf(id) === -1 ? addCheckedItem(id) : deleteCheckedItem(id));
    };
    const handleCheckboxAllClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isCheckedAll) {
            sortItemsIds.forEach((id) => {
                if (checkedItems.indexOf(id) !== -1) {
                    dispatch(deleteCheckedItem(id));
                }
            });
            return;
        }
        sortItemsIds.forEach((id) => {
            if (checkedItems.indexOf(id) === -1) {
                dispatch(addCheckedItem(id));
            }
        });
    };
    return (React.createElement(Box, { component: 'div', sx: (theme) => ({
            width: '100%',
            height: '100%',
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.simplebar-horizontal': {
                right: '10px',
            },
            '.simplebar-vertical': {
                top: '30px',
                bottom: '0',
            },
            '.MuiListItem-root': {
                width: '100%',
                userSelect: 'none',
                padding: theme.spacing(0),
                borderRadius: '0',
            },
        }) },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(TransportListContext.Provider, { value: {
                    sortItemClick: handleSortClick,
                    sortSelectedItem,
                    onCheckboxAllClick: handleCheckboxAllClick,
                    isCheckedAll,
                    isIndeterminate,
                    columns,
                } },
                React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(FixedSizeList, { height: height, itemSize: 24, overscanCount: 10, itemCount: sortItems.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, innerElementType: TransportListInnerElementType, itemData: {
                        items: sortItems,
                        selectedItem,
                        onClick: handleItemClick,
                        onCheckboxClick: handleCheckboxClick,
                        checkedItems,
                        columns,
                    } }, TransportListItemRender)))))))));
};
const TransportListInnerElementType = forwardRef(function TransportListInnerElementType(_a, ref) {
    var { children, style } = _a, rest = __rest(_a, ["children", "style"]);
    return (React.createElement(TransportListContext.Consumer, null, (context) => {
        if (!context) {
            return null;
        }
        const { sortItemClick, sortSelectedItem, onCheckboxAllClick, isCheckedAll, isIndeterminate, columns } = context;
        return (React.createElement(List, Object.assign({ ref: ref, disablePadding: true, sx: {
                height: style === null || style === void 0 ? void 0 : style.height,
                minHeight: '100%',
            } }, rest),
            React.createElement(ListItem, { sx: (theme) => ({
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: style === null || style === void 0 ? void 0 : style.height,
                    minHeight: '100%',
                    zIndex: '2',
                    pointerEvents: 'none',
                    padding: theme.spacing(0),
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        height: '100%',
                        position: 'relative',
                        flex: 'none',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            height: '100%',
                            bgcolor: theme.palette.divider,
                            width: '1px',
                            zIndex: '1',
                            pointerEvents: 'none',
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '&:before': {
                                display: 'none',
                            },
                        },
                    },
                }) },
                React.createElement(ListItemText, { style: { width: '58px' } }),
                columns.map((column) => (React.createElement(ListItemText, { key: column.name, style: { width: column.width, minWidth: column.width } })))),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    width: '100%',
                    display: 'flex',
                    position: 'sticky !important',
                    top: '0',
                    zIndex: '1',
                    '.MuiListItemButton-root': {
                        display: 'flex',
                        flex: 'none',
                        padding: theme.spacing(0),
                        position: 'relative',
                        bgcolor: 'background.default',
                        borderColor: theme.palette.divider,
                        '&:first-of-type': {
                            '.MuiListItemText-root': {
                                paddingLeft: '31px',
                            },
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '.MuiListItemText-root': {
                                paddingRight: theme.spacing(2),
                            },
                        },
                        '.MuiTouchRipple-rippleVisible': {
                            color: 'inherit',
                        },
                        '.MuiListItemText-root': {
                            display: 'flex',
                            cursor: 'pointer',
                            margin: theme.spacing(0),
                            padding: '6px 6px',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                display: 'flex',
                                '&:nth-of-type(1)': {
                                    color: 'text.secondary',
                                    marginRight: '4px',
                                },
                                '&:nth-of-type(2) svg': {
                                    fontSize: '12px',
                                    transition: 'all 300ms',
                                    transform: 'rotateZ(0deg)',
                                },
                            },
                        },
                    },
                }) },
                React.createElement(ListItemButton, { disableTouchRipple: true, style: { minWidth: '58px', minHeight: '30px' }, onClick: onCheckboxAllClick },
                    React.createElement(ListItemText, { disableTypography: true, sx: {
                            display: 'flex',
                            justifyContent: 'flex-end !important',
                            marginLeft: 'auto',
                            padding: '0 !important',
                            paddingRight: '10px !important',
                        } },
                        React.createElement(Protect, { permissions: ['create', 'delete', 'update'], renderPermitted: () => (React.createElement(React.Fragment, null,
                                React.createElement(Checkbox, { size: 'small', color: 'primary', disableRipple: true, checked: isCheckedAll, indeterminate: isIndeterminate, onClick: onCheckboxAllClick, sx: {
                                        padding: 0,
                                        marginLeft: '-2px',
                                        svg: {
                                            fontSize: '18px',
                                        },
                                    } }))), renderForbidden: () => React.createElement(React.Fragment, null) }))),
                columns.map((column) => (React.createElement(ListItemButton, { key: column.name, onClick: sortItemClick(column), disableTouchRipple: !column.order, sx: (theme) => ({
                        minWidth: column.width,
                        '.MuiListItemText-root': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                width: 'auto',
                                '&:nth-of-type(2)': Object.assign({ opacity: '0.2', transform: 'rotateZ(0)', transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms' }, (sortSelectedItem.name === column.name && Object.assign({ opacity: '1' }, (sortSelectedItem.order === 'DESC' && {
                                    transform: 'rotateZ(180deg)',
                                })))),
                            },
                        },
                    }) },
                    React.createElement(ListItemText, { disableTypography: true },
                        React.createElement(Typography, null, column.label),
                        React.createElement(Typography, null, column.order && React.createElement(ArrowUpward, { fontSize: 'small' }))))))),
            children));
    }));
});
TransportListInnerElementType.displayName = 'TransportListInnerElementType';
function renderGroupTypeIcon(groupType, color) {
    switch (groupType) {
        case 'circle':
            return React.createElement(TransportCircleIcon, { color: color });
        case 'triangle':
            return React.createElement(TransportTriangleIcon, { color: color });
        case 'square':
            return React.createElement(TransportSquareIcon, { color: color });
        default:
            return React.createElement(TransportCircleIcon, { sx: { opacity: 0 } });
    }
}
const TransportListItemRender = ({ data, index, style }) => {
    const { items, selectedItem, onClick, onCheckboxClick, checkedItems, columns } = data;
    const item = items[index];
    return (React.createElement(ListItem, { key: item.id, disablePadding: true, style: style, selected: item.id === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id), onClick: onClick(item), sx: (theme) => (Object.assign({ width: '100%', marginTop: '30px', userSelect: 'none', borderRadius: '0', '&:hover': {
                '.MuiListItemText-root': {
                    cursor: 'pointer',
                },
            }, '&.Mui-selected': {
                '.MuiListItemText-root': {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                },
                '.MuiCheckbox-root': {
                    color: theme.palette.primary.contrastText,
                    '&.Mui-checked': {
                        color: theme.palette.primary.contrastText,
                    },
                },
            }, '.MuiListItemText-root': {
                display: 'flex',
                flex: 'none',
                height: '100%',
                margin: theme.spacing(0),
                padding: theme.spacing(0.5, 1),
                alignItems: 'center',
                '&:first-of-type': {
                    paddingLeft: theme.spacing(2),
                    svg: {
                        fontSize: '16px',
                        marginRight: '3px',
                    },
                },
                '&:last-of-type': {
                    flex: '1',
                    paddingRight: theme.spacing(2),
                },
                '.MuiTypography-root': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
                '.MuiCheckbox-root': {
                    padding: theme.spacing(0),
                    marginLeft: '-2px',
                    svg: {
                        fontSize: '18px',
                    },
                },
            } }, ((items.length - (items.length - 1 - index)) % 2 === 0 && {
            '& .MuiListItemText-root': {
                bgcolor: 'background.default',
            },
        }))) },
        React.createElement(ListItemText, { disableTypography: true, style: { width: '58px', justifyContent: 'center' } },
            renderGroupTypeIcon(item.group_type, item.color),
            React.createElement(Protect, { permissions: ['create', 'delete', 'update'], renderPermitted: () => (React.createElement(React.Fragment, null,
                    React.createElement(Checkbox, { size: 'small', color: 'primary', checked: checkedItems.indexOf(item.id) !== -1, onClick: onCheckboxClick(item.id), disableRipple: true }))), renderForbidden: () => React.createElement(React.Fragment, null) })),
        columns.map((column) => {
            var _a;
            return (React.createElement(ListItemText, { key: column.name, style: { width: column.width, minWidth: column.width } },
                React.createElement(Typography, null, column.format ? column.format(item[column.name]) : String((_a = item[column.name]) !== null && _a !== void 0 ? _a : ''))));
        })));
};
export default TransportList;
